import { Heart, Sparkles, Users, GraduationCap, ExternalLink } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { PageHeader, SectionLabel } from "../components/PageHeader";
import { Reveal } from "../lib/motionx";
import { useLang } from "../i18n";

const perks = [
  { icon: Heart, title: "Mission-driven work", body: "Make a real difference in the lives of children and families every single day." },
  { icon: Users, title: "Supportive team", body: "Join a warm, collaborative team that has your back and celebrates your growth." },
  { icon: GraduationCap, title: "Learning & growth", body: "Opportunities to develop your skills and advance in a caring, expert environment." },
  { icon: Sparkles, title: "Meaningful moments", body: "Be there for the milestones — the first words, the big steps, the proud smiles." },
];

const careerLinks = [
  {
    label: "Join Amenity Health Services",
    href: "https://www.paycomonline.net/v4/ats/web.php/portal/C16F84A9363160ECE4EE283E8480BB44/career-page",
    testid: "join-health-services-btn",
  },
  {
    label: "Join Amenity ABA Center",
    href: "https://www.paycomonline.net/v4/ats/web.php/portal/10A3DE6511E7D6C24F2144B34E6A7836/career-page",
    testid: "join-aba-center-btn",
  },
];

export default function JoinTeam() {
  const { t } = useLang();
  useSeo({
    title: "Join Our Team | Careers at Amenity Health Services",
    description:
      "Grow your career with Amenity Health Services in Houston, TX. Explore roles in ABA, speech, occupational, and physical therapy, and apply today.",
  });

  return (
    <>
      <PageHeader
        label="Careers"
        titleLines={["Join our", "team"]}
        intro="We're always looking for compassionate, dedicated people who share our passion for helping children thrive. If that sounds like you, we'd love to hear from you."
      />

      <section className="py-10 sm:py-16 bg-white/60">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel className="mb-5">{t("Why work with us")}</SectionLabel>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-ink">
              {t("A place to do work that matters")}
            </h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {perks.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-black/5 bg-cream p-7">
                  <span className="grid place-items-center h-12 w-12 rounded-2xl bg-teal/10 text-teal mb-5">
                    <p.icon size={22} />
                  </span>
                  <h3 className="font-heading font-semibold text-lg text-ink">{t(p.title)}</h3>
                  <p className="mt-2 text-sm text-ink/60 leading-relaxed">{t(p.body)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <div className="rounded-[2rem] border border-black/5 bg-cream p-8 sm:p-12 text-center">
              <h2 className="font-heading font-semibold text-2xl sm:text-3xl tracking-tight text-ink">
                {t("Ready to apply?")}
              </h2>
              <p className="mt-3 text-ink/60 leading-relaxed max-w-xl mx-auto">
                {t("Choose the team you'd like to join and you'll be taken to our careers portal to view openings and apply.")}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
                {careerLinks.map((c) => (
                  <a
                    key={c.testid}
                    href={c.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={c.testid}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-teal px-7 py-4 font-medium text-white hover:bg-[#2E6088] transition-colors"
                  >
                    {t(c.label)} <ExternalLink size={17} />
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
